// @owners { team: patients }
import { COLORS, SPACING } from '@alto/design-library-tokens';
import {
  Body,
  Column,
  Icon,
  ListButton,
  ListPage,
  MdPadding,
  Row,
  SectionedPage,
  Separator,
  useScreenSize,
} from '@alto/design-system';
import { useNavigation } from '@alto/navigation';
import React, { useEffect } from 'react';
import { Linking } from 'react-native';
import { ALTO_INJECTION_GUIDES_URL } from '~shared/constants';
import { getPlatformOS } from '~shared/helpers/getPlatformOS';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { type OriginName } from '~shared/lib/analytics/src/getOrigin';
import { useQueryInstructionalVideos } from '../hooks/useQueryInstructionalVideos';
import { InstructionalVideo } from './InstructionalVideo';

const MaxWidth = 600 - SPACING.STATIC.XL.value;

export const InstructionalVideosPage = () => {
  const { navigate, canGoBack, goBack, getParam } = useNavigation<'RouteInstructionalVideos'>();
  const { isMDScreenOrBigger } = useScreenSize();
  const { trackPageView } = useAnalytics();

  const { videos } = useQueryInstructionalVideos();
  const isWeb = getPlatformOS() === 'web';
  const origin = getParam('origin');

  const openInjectionGuidesLink = () => {
    Linking.openURL(ALTO_INJECTION_GUIDES_URL);
  };

  const showInstructionalVideosPage = videos && videos.length > 0;
  const videoIDs = videos ? videos.map((video) => video.id).join(',') : videos;
  useEffect(() => {
    if (!showInstructionalVideosPage) return;
    trackPageView({
      event: EVENTS.INSTRUCTIONAL_VIDEOS_PAGE_VIEWED,
      params: { videoIDs, origin },
    });
  }, [trackPageView, videoIDs, origin, showInstructionalVideosPage]);

  if (!showInstructionalVideosPage) {
    return null;
  }

  return (
    <SectionedPage
      headerBackgroundColor={COLORS.PALETTE.GREYSCALE.LIGHTEST}
      HeaderContent={
        <MdPadding bottomPadding={SPACING.STATIC.SM}>
          <>
            <ListPage.Header.Content.Title>Injection guides</ListPage.Header.Content.Title>
            <Row>
              <Body>For your medications</Body>
            </Row>
          </>
        </MdPadding>
      }
      dismissIcon="chevronleft"
      onDismiss={() => {
        // Web can directly go back. Native needs to check if it can go back otherwise navigate to the homescreen
        if (isWeb || canGoBack()) {
          goBack();
        } else {
          navigate('RouteAppTabNavigator', { screen: 'RouteTabHomescreen' });
        }
      }}
    >
      <ListPage.Content.FlatList
        data={videos}
        ItemSeparatorComponent={Separator}
        renderItem={({ item, index }) => (
          <InstructionalVideo
            video={item}
            index={index}
            origin={origin as OriginName}
          />
        )}
        keyExtractor={(item) => item.id.toString()}
      />
      <Column>
        <Separator />
        <MdPadding>
          <Row center>
            <ListButton
              // eslint-disable-next-line react-native/no-inline-styles
              style={{ width: '100%', maxWidth: isMDScreenOrBigger ? MaxWidth : '100%' }}
              label="View full video library"
              LeftContent={<Icon name="play-duo" />}
              rightIcon="chevronright-small"
              onPress={openInjectionGuidesLink}
            />
          </Row>
        </MdPadding>
      </Column>
    </SectionedPage>
  );
};
